
import React, { useEffect, useState, useRef } from "react";
import { CiBellOn, CiSearch } from "react-icons/ci";
import axios from "axios";

type User = {
  id?: number | string;
  name?: string;
  company_name?: string;
  profile_img?: string;
  email?: string;
  mobile?: string;
  role?: string;
  [key: string]: any;
};

type Notification = {
  id: string;
  title: string;
  description?: string;
  created_at: string;
  read?: boolean;
  type: string;
};

interface HeaderProps {
  searchValue?: string;
  onSearchChange?: (val: string) => void;
}

// Provide: get seen notif IDs from localStorage
const NOTIF_LS_KEY = "seen_notif_ids_demo";
function getSeenNotifIds(): string[] {
  try {
    const s = localStorage.getItem(NOTIF_LS_KEY);
    if (s) return JSON.parse(s);
  } catch {}
  return [];
}
function markNotifAsSeen(id: string) {
  let seen = getSeenNotifIds();
  if (!seen.includes(id)) {
    seen.push(id);
    try {
      localStorage.setItem(NOTIF_LS_KEY, JSON.stringify(seen));
    } catch {}
  }
}

// --- Helper: decode jwt (very simple, no validation) ---
function decodeToken(token: any) {
  if (!token) return {};
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return {};
  }
}

const Header: React.FC<HeaderProps> = ({
  searchValue = "",
  onSearchChange = () => {},
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [settingLogo, setSettingLogo] = useState<string | null>(null);

  const [showNotif, setShowNotif] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(() => {
    return localStorage.getItem("selected_company_id") || "all";
  });
  const [companyList, setCompanyList] = useState<any[]>([]);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (selectedCompanyId && selectedCompanyId !== "all") {
      axios.defaults.headers.common['x-selected-company-id'] = selectedCompanyId;
    } else {
      delete axios.defaults.headers.common['x-selected-company-id'];
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (user && (user.role === "super-admin" || !user.company_id)) {
      axios.get(`${API_URL}/auth/companies`)
        .then(res => {
          setCompanyList(Array.isArray(res.data) ? res.data : []);
        })
        .catch(err => console.error("Error loading companies list:", err));
    }
  }, [user]);

  const handleSelectCompany = (id: string) => {
    setSelectedCompanyId(id);
    localStorage.setItem("selected_company_id", id);
    window.location.reload();
  };

  // On mount: hydrate user from token & API
  useEffect(() => {
    async function fetchUserData() {
      try {
        let userData: User | null = null;
        const token = localStorage.getItem("token");
        let decoded: any = null;
        if (token) {
          decoded = decodeToken(token);
        }
        const role = decoded.role;

        if (decoded && role === "company") {
          // Fetch from /companies, filter by res.userId
          const res = await axios.get(`${API_URL}/auth/companies`);
          const companyId =
            decoded.userId ||
            decoded.id ||
            decoded.companyId ||
            decoded.company_id ||
            decoded.cid;
          let company =
            (Array.isArray(res.data)
              ? res.data
              : []
            ).find((c: any) =>
              String(c.id) === String(companyId)
            );
          if (company) {
            userData = {
              ...company,
              id: company.id ?? company.company_id,
              name: company.company_name || company.name || "Company",
              company_name: company.company_name || company.name || "Company",
              role: "company",
              profile_img: company.profile_img || "",
              email: company.email || "",
              mobile: company.mobile || company.phone || "",
            };
          }
        } else if (decoded) {
          // Not a "company" role, fetch from /allusersinfo and match token id
          const res = await axios.get(`${API_URL}/auth/allusersinfo`);
          let userId =
            decoded.userId ||
            decoded.id ||
            decoded.userid ||
            decoded.uid ||
            decoded.user_id;
          let userFound =
            (Array.isArray(res.data)
              ? res.data
              : []
            ).find((u: any) => String(u.id) === String(userId));
          if (userFound) {
            userData = {
              ...userFound,
              name: userFound.username || "User",
              role: userFound.role || "user",
              profile_img: userFound.profile_img || "",
              email: userFound.email || "",
              mobile: userFound.mobile || "",
            };
          }
        }
        setUser(userData);
      } catch (error) {
        setUser(null);
      }
    }
    fetchUserData();
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/auth/setting`)
      .then(res => {
        if (res.data && res.data.logo) {
          setSettingLogo(res.data.logo);
        }
      })
      .catch(err => console.error("Error loading settings logo:", err));
  }, []);

  // Dismiss notification dropdown on outside click
  useEffect(() => {
    if (!showNotif) return;
    const handler = (e: MouseEvent) => {
      if (
        notifRef.current &&
        e.target instanceof Node &&
        !notifRef.current.contains(e.target)
      ) {
        setShowNotif(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotif]);

  // --- Edit Profile logic ---
  function openEdit() {
    if (user) {
      setEditUser({ ...user });
      setShowEdit(true);
      setEditError(null);
    }
  }
  function closeEdit() {
    setShowEdit(false);
    setEditError(null);
  }
  function handleEditInput(e: React.ChangeEvent<HTMLInputElement>) {
    setEditUser((prev) =>
      prev ? { ...prev, [e.target.name]: e.target.value } : prev
    );
  }
  function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editUser) return;
    setIsSubmitting(true);
    setEditError(null);
    setTimeout(() => {
      setUser(editUser);
      setShowEdit(false);
      setIsSubmitting(false);
    }, 700);
  }

  // UI presentation
  const role = user?.role;
  const userName = user?.name || "User";
  const companyName = user?.company_name;
  const logoLabel =
    role === "company"
      ? (companyName || userName)
      : userName;

  const getDisplayCompanyName = () => {
    if (user && user.company_id) {
      return user.mapped_company_name || user.company_name || "Mapped Company";
    }
    const activeCompany = companyList.find(c => String(c.id) === String(selectedCompanyId));
    return activeCompany ? activeCompany.company_name : "All Companies Workspace";
  };

  // Get logo based on active workspace selection
  const getActiveLogo = () => {
    if (user && user.role === "company" && user.profile_img) {
      return user.profile_img;
    }
    if (selectedCompanyId && selectedCompanyId !== "all") {
      const activeCompany = companyList.find(c => String(c.id) === String(selectedCompanyId));
      if (activeCompany && (activeCompany.profile_img || activeCompany.logo)) {
        return activeCompany.profile_img || activeCompany.logo;
      }
    }
    return settingLogo;
  };

  const displayLogo = getActiveLogo();

  // Modern logo styling: clean, black, reduced font size, modern typeface
  return (
    <>
      <header className="flex flex-wrap justify-between items-center p-4 gap-y-4 border-b-2 border-[#ECEFF9] bg-white z-30 flex-shrink-0">
        <div className="w-full md:w-[48%] lg:w-[16%] flex-shrink-0 flex items-center pl-16 md:pl-0">
          {displayLogo ? (
            <img 
              src={displayLogo.startsWith('http') ? displayLogo : `${API_URL}${displayLogo}`} 
              alt="Logo" 
              className="max-h-12 object-contain"
            />
          ) : logoLabel ? (
            <span
              className="
                text-[22px]
                md:text-[28px]
                font-extrabold
                tracking-wider
                select-none
              "
              style={{
                fontFamily: "'Montserrat', 'Quicksand', 'Poppins', 'Segoe UI', 'Arial', sans-serif",
                letterSpacing: "0.08em",
                fontVariant: "small-caps",
                color: "#3730a3",
              }}
            >
              {logoLabel}
            </span>
          ) : (
            <span className="text-2xl font-bold text-gray-800">Welcome</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {user && (user.role === "super-admin" || !user.company_id) && (
            <div className="flex items-center gap-2 bg-indigo-50/30 px-3 py-1.5 rounded-xl border border-indigo-100/50">
              <span className="text-xs font-bold text-indigo-900/60 uppercase tracking-wider hidden sm:inline">Active Workspace:</span>
              <select
                value={selectedCompanyId}
                onChange={(e) => handleSelectCompany(e.target.value)}
                className="border-2 border-indigo-100 rounded-xl bg-white p-1.5 font-bold text-indigo-950 focus:border-indigo-300 focus:outline-none text-xs cursor-pointer shadow-sm"
              >
                <option value="all">🌐 All Companies</option>
                {companyList.map((comp) => (
                  <option key={comp.id} value={comp.id}>
                    🏢 {comp.company_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {user && (
            <div className="flex items-center gap-2.5 bg-emerald-50 px-3.5 py-1.5 rounded-xl border border-emerald-100 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span className="text-xs font-semibold text-emerald-900">
                Company: <strong className="text-emerald-950 font-extrabold">{getDisplayCompanyName()}</strong>
              </span>
            </div>
          )}
        </div>
        <div className="w-full md:w-[48%] lg:w-[22%] flex-shrink-0 hidden">
          <h1 className="text-[20px] sm:text-[22px] font-semibold">
            Welcome Back, {userName}!
          </h1>
        </div>
        <div className="hidden w-full md:w-[48%] lg:w-[27%] bg-[#F4F6F8] h-[38px] flex items-center rounded-[6px] px-4">
          <input
            type="text"
            placeholder="Search..."
            className="w-full bg-transparent h-[32px] focus:outline-none"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <CiSearch className="text-[#8488B7] text-[24px] cursor-pointer" />
        </div>
        <div className="w-full md:w-[48%] lg:w-[24%] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <img
              className="w-[40px] h-[40px] object-cover rounded-full"
              src={
                user?.profile_img
                  ? user.profile_img
                  : `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}`
              }
              alt="Profile"
            />
            <div className="flex flex-col">
              <span className="hidden sm:inline text-sm sm:text-base font-poppins">
                {userName}
              </span>
              <button
                className="text-xs text-blue-600 mt-0.5 underline text-left"
                style={{
                  padding: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
                onClick={openEdit}
              >
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* --- Edit Profile Modal --- */}
      {showEdit && editUser && (
        <div
          className="fixed z-50 inset-0 flex items-center justify-center bg-black bg-opacity-40"
          style={{ animation: "fadeIn .2s" }}
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full border shadow-md relative">
            <button
              onClick={closeEdit}
              className="absolute right-3 top-2 text-gray-400 hover:text-gray-700 text-lg"
              aria-label="Close"
              style={{ background: "none", border: "none" }}
            >
              &times;
            </button>
            <h2 className="font-bold text-xl mb-4 text-[#6742c7]">
              Edit Profile
            </h2>
            <form onSubmit={submitEdit} className="flex flex-col gap-3">
              <label className="text-sm font-medium flex flex-col gap-1">
                Name
                <input
                  name="name"
                  type="text"
                  value={editUser.name || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                  required
                />
              </label>
              <label className="text-sm font-medium flex flex-col gap-1">
                Email
                <input
                  name="email"
                  type="email"
                  value={editUser.email || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                />
              </label>
              <label className="text-sm font-medium flex flex-col gap-1">
                Mobile
                <input
                  name="mobile"
                  type="text"
                  value={editUser.mobile || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                />
              </label>
              <label className="text-sm font-medium flex flex-col gap-1">
                Profile Image URL
                <input
                  name="profile_img"
                  type="url"
                  value={editUser.profile_img || ""}
                  onChange={handleEditInput}
                  className="border rounded px-2 py-1"
                  placeholder="https://example.com/img.png"
                />
              </label>
              {editError && (
                <div className="text-red-600 text-xs">{editError}</div>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  className="px-3 py-1 border rounded text-gray-600 bg-gray-100 hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#6742c7] text-white px-3 py-1 rounded hover:bg-[#5432a7] disabled:opacity-50"
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
